import React, { useState } from "react";
import { motion } from "framer-motion"; //  Import Framer Motion
import { registerUser } from "services/authService";
import { Link } from "react-router-dom";
import logo from "assets/logo/logo.png";
import * as Sentry from "@sentry/react";

const Signup = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  //  Email Validation
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  //  Phone Number Validation (E.164 Format)
  const isValidPhone = (phone) => /^\+?[1-9]\d{1,14}$/.test(phone);

  const handleSignup = async (e) => {
    e.preventDefault();

    if (!isValidEmail(email)) {
      setError("Invalid email format. Please enter a valid email.");
      return;
    }

    if (!isValidPhone(phone)) {
      setError(
        "Invalid phone number format. Use international format: +1234567890"
      );
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    try {
      await registerUser(email, password, `${firstName} ${lastName}`, phone);
      setSuccess(true);
      setError("");
    } catch (error) {
      Sentry.captureException(error);
      setError(error.message);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-800 w-full">
      {/* Logo Animation */}
      <motion.img
        src={logo}
        alt="Logo"
        className="w-64 mb-4"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      />

      {/* Animated Success Message or Signup Form */}
      <motion.div
        className="max-w-md p-6 mx-2 bg-white rounded-lg shadow-xl"
        initial={{ opacity: 0, y: 50 }} // Start slightly below
        animate={{ opacity: 1, y: 0 }} // Fade in and move up
        transition={{ duration: 0.8, ease: "easeOut" }} // Smooth transition
      >
        {success ? (
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-green-600 mb-4">
              Registration Successful! 
            </h2>
            <p className="text-gray-700 mb-4">
              Please check your email to verify your account and login.
            </p>
            <Link
              to="/login"
              className="py-2 px-6 text-lg text-center font-semibold bg-green-700 hover:bg-green-600 text-white rounded-lg shadow-xl transition"
            >
              Login
            </Link>
          </div>
        ) : (
          <>
            <h2 className="text-2xl font-semibold text-center text-gray-700 mb-6">
              Sign Up
            </h2>

            {error && (
              <p className="text-red-500 text-sm text-center mb-4">{error}</p>
            )}

            <form onSubmit={handleSignup} className="space-y-4">
              <input
                type="text"
                placeholder="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <input
                type="text"
                placeholder="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-2 ${
                  email && !isValidEmail(email)
                    ? "border-red-500 ring-red-500"
                    : "focus:ring-blue-400"
                }`}
              />
              <input
                type="tel"
                placeholder="Phone number (+1234567890)"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-2 ${
                  phone && !isValidPhone(phone)
                    ? "border-red-500 ring-red-500"
                    : "focus:ring-blue-400"
                }`}
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <input
                type="password"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-2 ${
                  confirmPassword && password !== confirmPassword
                    ? "border-red-500 ring-red-500"
                    : "focus:ring-blue-400"
                }`}
              />
              <button
                type="submit"
                className="w-full p-2 text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition"
              >
                Sign Up
              </button>
            </form>

            <p className="mt-4 text-center text-sm text-gray-600">
              Already have an account?{" "}
              <Link to="/login" className="text-blue-500 hover:underline">
                Login here
              </Link>
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default Signup;
